import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, Loader2, CheckCircle2 } from "lucide-react";
import { adminApi, formatApiError } from "../adminApi";

const FIELD = "w-full rounded-lg border border-[#E5E7EB] px-4 py-3 text-[#1F2937] focus:outline-none focus:border-[#1E88E5] focus:ring-2 focus:ring-[#1E88E5]/20 transition";

export default function AdminResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) return setError("Password must be at least 8 characters");
    if (pw !== confirm) return setError("Passwords do not match");
    setLoading(true);
    try {
      await adminApi.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/admin/login"), 2000);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A2342] flex items-center justify-center px-6" data-testid="admin-reset-page">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-8">
          <ShieldCheck className="text-[#1E88E5]" size={28} />
          <span className="text-white font-heading font-bold tracking-wide">MERIDIAN VANGUARD ADMIN</span>
        </div>
        <div className="bg-white rounded-xl p-8 shadow-xl">
          {done ? (
            <div className="text-center">
              <CheckCircle2 className="mx-auto text-green-600" size={40} />
              <h1 className="mt-4 font-heading text-xl font-bold text-[#0A2342]">Password updated</h1>
              <p className="mt-2 text-sm text-[#4B5563]">Redirecting to sign in…</p>
            </div>
          ) : !token ? (
            <div className="text-center">
              <p className="text-[#4B5563]">Invalid reset link.</p>
              <Link to="/admin/forgot-password" className="mt-4 inline-block text-sm text-[#1E88E5] hover:underline">Request a new link</Link>
            </div>
          ) : (
            <>
              <h1 className="font-heading text-2xl font-bold text-[#0A2342]">Set a new password</h1>
              <form onSubmit={onSubmit} className="mt-6 space-y-4" noValidate>
                <input data-testid="reset-password" className={FIELD} type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" />
                <input data-testid="reset-confirm" className={FIELD} type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm password" />
                {error && <p className="text-sm text-red-700">{error}</p>}
                <button data-testid="reset-submit" type="submit" disabled={loading} className="w-full flex items-center justify-center gap-2 rounded-lg bg-[#0A2342] text-white py-3 font-semibold hover:bg-[#1E88E5] transition-colors disabled:opacity-60">
                  {loading && <Loader2 size={18} className="animate-spin" />} Update Password
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
